import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import {
  get_handover,
  getUnpaidInvoicesForUnit,
  update_handover,
  upload_handover_images,
  getUnitMeterHistory,
  get_all_handovers,
  backend_url,
  GET_CURRENCIES_URL
} from '../../../utils/urls';
import Layout from '../component/layout';

const EditHandover = ({ id: propId, location: propLocation, onClose }) => {
  // Use props if provided, otherwise use router hooks
  const routeParams = useParams();
  const routeLocation = useLocation();
  const navigate = useNavigate();

  // Prioritize props over router values
  const id = propId || routeParams.id;
  const location = propLocation || routeLocation;

  const fileUploadRef = useRef(null);
  const toast = useRef(null);
  const propertyManagerSigRef = useRef(null);
  const customerSigRef = useRef(null);
  const propertyManagerFileRef = useRef(null);
  const customerFileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [handover, setHandover] = useState(null);
  const [displayDialog, setDisplayDialog] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [facilityId, setFacilityId] = useState('');

  // Image viewer state
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedItemImages, setSelectedItemImages] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');

  // Smart meter state
  const [waterMeterData, setWaterMeterData] = useState(null);
  const [powerMeterData, setPowerMeterData] = useState(null);
  const [meterDataLoading, setMeterDataLoading] = useState(false);
  const [previousReadings, setPreviousReadings] = useState({
    water: [],
    electricity: []
  });

  // Unpaid invoices state
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [showInvoiceDeductionsDialog, setShowInvoiceDeductionsDialog] = useState(false);

  // Invoice type filters state
  const [selectedInvoiceTypes, setSelectedInvoiceTypes] = useState([
    'rent', 'utilities', 'maintenance', 'service', 'penalty', 'deposit', 'other'
  ]);
  const [showInvoiceTypeFilter, setShowInvoiceTypeFilter] = useState(false);

  // Available invoice types with icons and colors
  const invoiceTypes = [
    { label: 'Rent', value: 'rent', icon: 'ti-home', color: 'primary' },
    { label: 'Utilities', value: 'utilities', icon: 'ti-bolt', color: 'warning' },
    { label: 'Maintenance', value: 'maintenance', icon: 'ti-tools', color: 'info' },
    { label: 'Service Fees', value: 'service', icon: 'ti-briefcase', color: 'success' },
    { label: 'Penalties', value: 'penalty', icon: 'ti-exclamation-circle', color: 'danger' },
    { label: 'Deposits', value: 'deposit', icon: 'ti-piggy-bank', color: 'secondary' },
    { label: 'Other', value: 'other', icon: 'ti-dots', color: 'dark' }
  ];

  const [currencies, setCurrencies] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState(null);
  const defaultCurrencyCode =
    defaultCurrency?.currencyShortCode ||
    handover?.defaultCurrency?.currencyShortCode ||
    'USD';

  // Form data
  const [formData, setFormData] = useState({
    handoverDate: null,
    status: '',
    keysHandedOver: 0,
    notes: '',
    meterReadings: {
      electricity: { reading: '' },
      water: { reading: '' },
      gas: { reading: '' }
    },
    items: [],
    attachments: [],
    signatures: {
      propertyManager: {
        signature: '',
        date: null
      },
      customer: {
        signature: '',
        date: null
      }
    }
  });

  // For inventory items editing
  const [editingItem, setEditingItem] = useState(null);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [tempItem, setTempItem] = useState({
    name: '',
    category: '',
    condition: '',
    currentCondition: '',
    quantity: 1,
    notes: '',
    repairCost: 0,
    images: [],
    currencyId: null,
    currency: null
  });

  // For security deposit (move-out only)
  const [securityDeposit, setSecurityDeposit] = useState({
    amount: 0,
    deductions: [],
    refundAmount: 0
  });

  // For deduction editing
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [showDeductionDialog, setShowDeductionDialog] = useState(false);
  const [tempDeduction, setTempDeduction] = useState({
    reason: '',
    amount: 0,
    description: '',
    currencyId: null,
    currency: null
  });

  // Options for dropdowns
  const statusOptions = [
    { label: 'Draft', value: 'Draft' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Disputed', value: 'Disputed' },
    { label: 'Resolved', value: 'Resolved' }
  ];

  const conditionOptions = [
    { label: 'Excellent', value: 'Excellent' },
    { label: 'Good', value: 'Good' },
    { label: 'Fair', value: 'Fair' },
    { label: 'Poor', value: 'Poor' },
    { label: 'Damaged', value: 'Damaged' },
    { label: 'Non-functional', value: 'Non-functional' }
  ];

  const categoryOptions = [
    { label: 'Furniture', value: 'Furniture' },
    { label: 'Appliance', value: 'Appliance' },
    { label: 'Electronics', value: 'Electronics' },
    { label: 'Fixture', value: 'Fixture' },
    { label: 'Utility', value: 'Utility' },
    { label: 'Other', value: 'Other' }
  ];

  // Fetch facility ID on component mount
  useEffect(() => {
    const getFacilityId = async () => {
      const id = await getItem("selectedFacilityId");
      setFacilityId(id);
    };

    getFacilityId();
  }, []);

  useEffect(() => {
    const fetchCurrencies = async () => {
      if (!facilityId) return;
      try {
        const response = await makeRequest2(`${GET_CURRENCIES_URL}/${facilityId}`, 'GET');
        if (response?.success && response.data) {
          const currencyData = response.data.data || response.data;
          const currenciesArray = Array.isArray(currencyData) ? currencyData : [currencyData];
          setCurrencies(currenciesArray);
          const defaultCurr = currenciesArray.find(curr => curr.isDefaultCurrency);
          setDefaultCurrency(defaultCurr || currenciesArray[0] || null);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    };

    fetchCurrencies();
  }, [facilityId]);

  // Fetch handover data with retry logic
  useEffect(() => {
    const fetchHandover = async (retryCount = 0) => {
      const maxRetries = 3;
      try {
        setLoading(true);
        setInventoryLoading(true);

        // Get handover either from state or fetch from API
        let handoverData = location.state?.handover;

        if (!handoverData && id) {
          const response = await makeRequest2(`${get_handover}/${id}`, "GET", null, { retries: 2 });

          if (response?.success && response.data) {
            handoverData = response.data;
          } else {
            const errorMsg = response?.error || "Could not fetch handover details";

            // Retry on network errors
            if (retryCount < maxRetries && (errorMsg.includes('No response received') || errorMsg.includes('500'))) {
              console.log(`Retrying handover fetch (attempt ${retryCount + 1}/${maxRetries})`);
              setTimeout(() => fetchHandover(retryCount + 1), 1000 * (retryCount + 1));
              return;
            }

            toastify(errorMsg, "error");
            handleClose();
            return;
          }
        }

        if (!handoverData) {
          toastify("No handover data found", "error");
          handleClose();
          return;
        }

        // Validate handover data structure
        if (typeof handoverData !== 'object') {
          toastify("Invalid handover data received", "error");
          handleClose();
          return;
        }

        setHandover(handoverData);

        // Validate and sanitize inventory items
        let validatedItems = [];
        if (handoverData.items) {
          if (Array.isArray(handoverData.items)) {
            validatedItems = handoverData.items.filter(item => {
              // Ensure item has required fields and valid structure
              if (!item || typeof item !== 'object') return false;

              // Clean up any invalid ObjectId references
              if (typeof item._id === 'number') delete item._id;
              if (typeof item.id === 'number') delete item.id;

              return item.name || item.description; // At least one identifier
            });
          } else {
            console.warn('handoverData.items is not an array:', handoverData.items);
            toastify("Invalid inventory data format received", "warning");
          }
        }

        // Initialize form data with validated items
        const newFormData = {
          handoverDate: handoverData.handoverDate ? new Date(handoverData.handoverDate) : new Date(),
          status: handoverData.status || 'Draft',
          keysHandedOver: handoverData.keysHandedOver || 0,
          notes: handoverData.notes || '',
          meterReadings: {
            electricity: { reading: handoverData.meterReadings?.electricity?.reading || '' },
            water: { reading: handoverData.meterReadings?.water?.reading || '' },
            gas: { reading: handoverData.meterReadings?.gas?.reading || '' }
          },
          items: validatedItems,
          attachments: Array.isArray(handoverData.attachments) ? handoverData.attachments : [],
          signatures: {
            propertyManager: {
              signature: handoverData.signatures?.propertyManager?.signature || '',
              date: handoverData.signatures?.propertyManager?.date || null
            },
            customer: {
              signature: handoverData.signatures?.customer?.signature || '',
              date: handoverData.signatures?.customer?.date || null
            }
          }
        };

        setFormData(newFormData);

        // Show inventory loading feedback
        if (validatedItems.length > 0) {
          toastify(`Loaded ${validatedItems.length} inventory item(s)`, "success");
        } else if (handoverData.items && handoverData.items.length > 0) {
          toastify("Some inventory items had invalid data and were filtered out", "warning");
        }

        // Fetch smart meter data for all handovers (both move-in and move-out)
        if (handoverData.unitId && facilityId) {
          const unitId = typeof handoverData.unitId === 'object' ? handoverData.unitId._id : handoverData.unitId;

          // Fetch smart meter data for this unit
          fetchWaterMeterData(unitId);
          fetchPowerMeterData(unitId);
          fetchPreviousReadings(unitId);
        }

        // Initialize security deposit for move-out handovers
        if (handoverData.handoverType === 'MoveOut' && handoverData.securityDeposit) {
          setSecurityDeposit({
            amount: handoverData.securityDeposit.amount || 0,
            deductions: handoverData.securityDeposit.deductions || [],
            refundAmount: handoverData.securityDeposit.refundAmount || 0
          });

          // If this is a move-out handover, fetch unpaid invoices
          if (handoverData.unitId && handoverData.customerId && facilityId) {
            const unitId = typeof handoverData.unitId === 'object' ? handoverData.unitId._id : handoverData.unitId;
            const customerId = typeof handoverData.customerId === 'object' ? handoverData.customerId._id : handoverData.customerId;

            fetchUnpaidInvoices(unitId, customerId);
          }
        }
      } catch (error) {
        console.error('Error fetching handover details:', error);

        const errorMsg = error.message || 'Failed to fetch handover details';

        // Retry on network errors
        if (retryCount < maxRetries && (errorMsg.includes('Network Error') || errorMsg.includes('timeout'))) {
          console.log(`Retrying handover fetch due to error (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => fetchHandover(retryCount + 1), 1000 * (retryCount + 1));
          return;
        }

        toastify(`Error: ${errorMsg}`, 'error');
        handleClose();
      } finally {
        setLoading(false);
        setInventoryLoading(false);
      }
    };

    if (facilityId) {
      fetchHandover();
    }
  }, [id, location.state, facilityId]);

  // Auto-recalculate deductions when inventory items change
  useEffect(() => {
    if (formData.items && formData.items.length > 0) {
      // Process all items and create deductions for those with repair costs
      formData.items.forEach(item => {
        if (item.repairCost && parseFloat(item.repairCost) > 0) {
          // Check if deduction already exists to avoid duplicates
          const existingDeduction = securityDeposit.deductions?.find(ded =>
            ded.source === 'inventory_repair' &&
            ded.sourceItemId === (item.tempId || item.id || item._id)
          );

          if (!existingDeduction) {
            createDeductionFromRepairCost(item);
          }
        }
      });
    }
  }, [formData.items]);

  // Fetch smart meter data for water
  const fetchWaterMeterData = async (unitId) => {
    try {
      setMeterDataLoading(true);
      const response = await makeRequest2(`${getUnitMeterHistory}/${facilityId}/${unitId}`, 'GET');

      if (response && response.success && response.data && response.data.meter) {
        setWaterMeterData(response.data.meter);

        // Auto-fill current reading if available
        if (response.data.meter.currentReading) {
          handleMeterReadingChange('water', response.data.meter.currentReading);
        }

        toast.current.show({
          severity: 'success',
          summary: 'Smart Water Meter Found',
          detail: 'Water meter reading automatically fetched',
          life: 3000
        });
      } else {
        setWaterMeterData(null);
      }
    } catch (error) {
      console.error('Error fetching water meter data:', error);
      setWaterMeterData(null);
    } finally {
      setMeterDataLoading(false);
    }
  };

  // Fetch smart meter data for power
  const fetchPowerMeterData = async (unitId) => {
    try {
      setMeterDataLoading(true);
      const response = await makeRequest2(
        `/api/app/power_meter_management/controllers/power_meters/get_unit_meter/${unitId}`,
        'GET'
      );

      if (response && response.success && response.data.data) {
        setPowerMeterData(response.data.data);

        // Auto-fill current reading if available
        if (response.data.data.currentReading) {
          handleMeterReadingChange('electricity', response.data.data.currentReading);
        }

        toast.current.show({
          severity: 'success',
          summary: 'Smart Power Meter Found',
          detail: 'Power meter reading automatically fetched',
          life: 3000
        });
      } else {
        setPowerMeterData(null);
      }
    } catch (error) {
      console.error('Error fetching power meter data:', error);
      setPowerMeterData(null);
    } finally {
      setMeterDataLoading(false);
    }
  };

  // Fetch previous readings from past handovers
  const fetchPreviousReadings = async (unitId) => {
    try {
      const response = await makeRequest2(`${get_all_handovers}/${facilityId}`, "GET");

      if (response.success && response.data) {
        let handoversList = [];

        if (response.data.data && Array.isArray(response.data.data)) {
          handoversList = response.data.data;
        } else if (Array.isArray(response.data)) {
          handoversList = response.data;
        } else if (response.data.handovers && Array.isArray(response.data.handovers)) {
          handoversList = response.data.handovers;
        }

        // Filter handovers for this unit
        const unitHandovers = handoversList.filter(handover => {
          const handoverUnitId = typeof handover.unitId === 'object' ? handover.unitId?._id : handover.unitId;
          return String(handoverUnitId) === String(unitId);
        });

        // Extract meter readings from past handovers
        const waterReadings = [];
        const electricityReadings = [];

        unitHandovers.forEach(handover => {
          if (handover.meterReadings) {
            if (handover.meterReadings.water && handover.meterReadings.water.reading) {
              waterReadings.push({
                reading: handover.meterReadings.water.reading,
                date: handover.handoverDate || handover.createdAt,
                type: handover.handoverType || 'Unknown'
              });
            }
            if (handover.meterReadings.electricity && handover.meterReadings.electricity.reading) {
              electricityReadings.push({
                reading: handover.meterReadings.electricity.reading,
                date: handover.handoverDate || handover.createdAt,
                type: handover.handoverType || 'Unknown'
              });
            }
          }
        });

        // Sort by date (most recent first)
        waterReadings.sort((a, b) => new Date(b.date) - new Date(a.date));
        electricityReadings.sort((a, b) => new Date(b.date) - new Date(a.date));

        setPreviousReadings({
          water: waterReadings.slice(0, 5), // Keep only last 5 readings
          electricity: electricityReadings.slice(0, 5)
        });
      }
    } catch (error) {
      console.error('Error fetching previous readings:', error);
    }
  };

  // Function to fetch unpaid invoices
  const fetchUnpaidInvoices = async (unitId, customerId, invoiceTypeFilter = null) => {
    if (!unitId || !customerId || !facilityId) {
      // console.log('Missing required parameters for fetching invoices');
      setUnpaidInvoices([]);
      return [];
    }

    try {
      setIsLoadingInvoices(true);

      // console.log(`Fetching unpaid invoices for facilityId=${facilityId}, unitId=${unitId}, customerId=${customerId}`);

      const response = await makeRequest2(
        `${getUnpaidInvoicesForUnit}/${facilityId}?unitId=${unitId}&customerId=${customerId}&types=${(invoiceTypeFilter || selectedInvoiceTypes).join(',')}&statuses=unpaid,overdue,partially_paid`,
        "GET"
      );

      // console.log('Unpaid invoices API response:', response);

      if (response && response.success) {
        // Ensure we have an array
        const invoicesArray = Array.isArray(response.data) ? response.data : [];

        // console.log('Unpaid invoices loaded:', invoicesArray);
        setUnpaidInvoices(invoicesArray);

        if (invoicesArray.length > 0) {
          toast.current?.show({
            severity: 'info',
            summary: 'Unpaid Invoices Found',
            detail: `Found ${invoicesArray.length} unpaid invoices that can be deducted from security deposit`,
            life: 5000
          });
        }

        return invoicesArray;
      } else {
        // console.error('Failed to fetch unpaid invoices:', response?.error || 'Unknown error');
        setUnpaidInvoices([]);
        return [];
      }
    } catch (error) {
      // console.error('Error fetching unpaid invoices:', error);
      setUnpaidInvoices([]);
      return [];
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMeterReadingChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      meterReadings: {
        ...prev.meterReadings,
        [type]: { reading: value }
      }
    }));
  };

  const handleSecurityDepositChange = (field, value) => {
    setSecurityDeposit(prev => ({
      ...prev,
      [field]: value
    }));

    // Recalculate refund amount
    setTimeout(calculateRefundAmount, 0);
  };

  const calculateRefundAmount = () => {
    const totalDeductions = securityDeposit.deductions.reduce(
      (sum, deduction) => sum + (parseFloat(deduction.amount) || 0),
      0
    );

    const refundAmount = Math.max(
      (parseFloat(securityDeposit.amount) || 0) - totalDeductions,
      0
    ).toFixed(2);

    setSecurityDeposit(prev => ({
      ...prev,
      refundAmount
    }));
  };

  // Function to automatically create deductions from inventory repair costs
  const createDeductionFromRepairCost = (inventoryItem) => {
    if (!inventoryItem || !inventoryItem.repairCost || parseFloat(inventoryItem.repairCost) <= 0) {
      return;
    }

    const repairAmount = parseFloat(inventoryItem.repairCost);
    const itemName = inventoryItem.name || inventoryItem.itemName || 'Item';

    // Check if a deduction for this item already exists
    const existingDeductions = securityDeposit.deductions || [];
    const existingIndex = existingDeductions.findIndex(ded =>
      ded.source === 'inventory_repair' &&
      ded.sourceItemId === (inventoryItem.id || inventoryItem._id || inventoryItem.tempId)
    );

    const newDeductions = [...existingDeductions];

    if (existingIndex >= 0) {
      // Update existing deduction amount
      newDeductions[existingIndex] = {
        ...newDeductions[existingIndex],
        amount: repairAmount,
        reason: `Repair cost for ${itemName}`,
        description: `Repair cost for ${itemName}`,
        currencyId: inventoryItem.currencyId || inventoryItem.currency?._id || defaultCurrency?._id || null,
        currency: inventoryItem.currency || defaultCurrency || null
      };
    } else {
      // Create new deduction
      const newDeduction = {
        reason: `Repair cost for ${itemName}`,
        amount: repairAmount,
        description: `Repair cost for ${itemName}`,
        source: 'inventory_repair',
        sourceItemId: inventoryItem.id || inventoryItem._id || inventoryItem.tempId || Date.now(),
        createdAt: new Date().toISOString(),
        currencyId: inventoryItem.currencyId || inventoryItem.currency?._id || defaultCurrency?._id || null,
        currency: inventoryItem.currency || defaultCurrency || null
      };
      newDeductions.push(newDeduction);
    }

    setSecurityDeposit(prev => ({
      ...prev,
      deductions: newDeductions
    }));

    // Recalculate refund amount
    setTimeout(calculateRefundAmount, 0);
  };

  // Function to remove deductions when items are archived or repair cost removed
  const removeRepairDeduction = (inventoryItemId) => {
    const filteredDeductions = (securityDeposit.deductions || []).filter(ded =>
      !(ded.source === 'inventory_repair' && ded.sourceItemId === inventoryItemId)
    );

    setSecurityDeposit(prev => ({
      ...prev,
      deductions: filteredDeductions
    }));

    // Recalculate refund amount
    setTimeout(calculateRefundAmount, 0);
  };

  // Function to upload images for an item
  const uploadItemImages = async (images) => {
    if (!images || images.length === 0) return [];

    try {
      // Filter only images that need to be uploaded (have 'file' property)
      const imagesToUpload = images.filter(img => img.file);

      if (imagesToUpload.length === 0) {
        // Return existing URLs if no new images to upload
        return images.map(img => typeof img === 'string' ? img : img.preview || img.url);
      }

      const formData = new FormData();
      imagesToUpload.forEach((imageData, index) => {
        formData.append('images', imageData.file, imageData.name);
      });

      const response = await makeRequest2(
        `${upload_handover_images}/${facilityId}/${id}`,
        "POST",
        formData,
        {
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.success && response.data?.images) {
        // Combine existing URLs with newly uploaded URLs
        const existingUrls = images.filter(img => !img.file).map(img =>
          typeof img === 'string' ? img : img.preview || img.url
        );
        const newUrls = response.data.images.map(img => img.url);

        return [...existingUrls, ...newUrls];
      } else {
        console.error('Image upload failed:', response?.error);
        throw new Error(response?.error || 'Image upload failed');
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  };

  const uploadAttachments = async (attachments) => {
    if (!attachments || attachments.length === 0) return [];

    try {
      const attachmentsToUpload = attachments.filter(att => att.file);

      if (attachmentsToUpload.length === 0) {
        return attachments.map(att => ({
          name: att.name,
          fileName: att.fileName,
          fileUrl: att.fileUrl,
          uploadDate: att.uploadDate
        }));
      }

      const formData = new FormData();
      attachmentsToUpload.forEach((attachment) => {
        formData.append('images', attachment.file, attachment.fileName || attachment.file.name);
      });

      const response = await makeRequest2(
        `${upload_handover_images}/${facilityId}/${id}`,
        "POST",
        formData,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response?.success && response.data?.images) {
        const uploadedAttachments = attachmentsToUpload.map((att, index) => ({
          name: att.name,
          fileName: att.fileName,
          fileUrl: response.data.images[index]?.url || att.fileUrl,
          uploadDate: att.uploadDate
        }));

        const existingAttachments = attachments
          .filter(att => !att.file)
          .map(att => ({
            name: att.name,
            fileName: att.fileName,
            fileUrl: att.fileUrl,
            uploadDate: att.uploadDate
          }));

        return [...existingAttachments, ...uploadedAttachments];
      } else {
        throw new Error(response?.error || 'Attachment upload failed');
      }
    } catch (error) {
      console.error('Error uploading attachments:', error);
      throw error;
    }
  };

  // Item handling
  const openNewItemDialog = () => {
    setEditingItem(null);
    setTempItem({
      name: '',
      category: '',
      condition: '',
      currentCondition: '',
      quantity: 1,
      notes: '',
      repairCost: 0,
      images: [],
      currencyId: defaultCurrency?._id || null,
      currency: defaultCurrency || null
    });
    setShowItemDialog(true);
  };

  const openEditItemDialog = (item, index) => {
    setEditingItem(index);

    // Ensure images are in the correct format for the UI
    const processedItem = {
      ...item,
      images: (item.images || []).map(img => {
        // If image is already an object with preview, keep it as is
        if (typeof img === 'object' && img.preview) {
          return img;
        }
        // If image is a URL string, convert it to the expected format
        if (typeof img === 'string') {
          return {
            preview: img,
            name: img.split('/').pop() || 'image',
            size: 0
          };
        }
        return img;
      })
    };

    setTempItem({
      ...processedItem,
      currencyId: processedItem.currencyId || processedItem.currency?._id || defaultCurrency?._id || null,
      currency: processedItem.currency || defaultCurrency || null
    });
    setShowItemDialog(true);
  };

  const handleItemChange = (field, value) => {
    setTempItem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Image handling functions
  const handleItemImageUpload = (event) => {
    const files = Array.from(event.files);
    const imageData = files.map(file => ({
      file: file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size
    }));
    setTempItem(prev => ({
      ...prev,
      images: [...(prev.images || []), ...imageData]
    }));
    event.options.clear();
  };

  const removeItemImage = (index) => {
    setTempItem(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };


  // Update attachment name
  const updateAttachmentName = (index, name) => {
    setFormData(prev => {
      const newAttachments = [...prev.attachments];
      newAttachments[index] = {
        ...newAttachments[index],
        name: name
      };
      return { ...prev, attachments: newAttachments };
    });
  };

  const saveItem = () => {
    // Validate item
    if (!tempItem.name || !tempItem.condition) {
      toastify("Name and condition are required for inventory items", "warning");
      return;
    }

    const newItems = [...formData.items];
    let itemToProcess = { ...tempItem };

    if (editingItem !== null) {
      // Update existing item
      const existingItem = newItems[editingItem];
      itemToProcess.tempId = existingItem.tempId || existingItem.id || existingItem._id || `temp_${Date.now()}`;
      newItems[editingItem] = itemToProcess;
    } else {
      // Add new item with temp ID for tracking
      itemToProcess.tempId = `temp_${Date.now()}`;
      newItems.push(itemToProcess);
    }

    setFormData(prev => ({
      ...prev,
      items: newItems
    }));

    // Automatically create deduction if item has repair cost
    if (itemToProcess.repairCost && parseFloat(itemToProcess.repairCost) > 0) {
      createDeductionFromRepairCost(itemToProcess);
    } else if (editingItem !== null) {
      // If repair cost was removed, remove existing deduction
      const itemId = itemToProcess.tempId || itemToProcess.id || itemToProcess._id;
      removeRepairDeduction(itemId);
    }

    setShowItemDialog(false);
  };

  const archiveItem = (index) => {
    const newItems = [...formData.items];
    const itemToArchive = newItems[index];

    // Remove any repair cost deduction for this item before archiving
    if (itemToArchive) {
      const itemId = itemToArchive.tempId || itemToArchive.id || itemToArchive._id;
      if (itemId) {
        removeRepairDeduction(itemId);
      }
    }

    // Instead of deleting, mark as archived with history
    newItems[index] = {
      ...newItems[index],
      isArchived: true,
      archivedAt: new Date().toISOString(),
      archivedBy: 'Current User' // This should be the actual user info
    };

    setFormData(prev => ({
      ...prev,
      items: newItems.filter(item => !item.isArchived) // Hide archived items from view
    }));

    // Show confirmation message
    toastify("Item archived successfully. It will be kept in history.", "info");
  };

  // Deduction handling
  const openNewDeductionDialog = () => {
    setEditingDeduction(null);
    setTempDeduction({
      reason: '',
      amount: 0,
      description: '',
      currencyId: defaultCurrency?._id || null,
      currency: defaultCurrency || null
    });
    setShowDeductionDialog(true);
  };

  const openEditDeductionDialog = (deduction, index) => {
    setEditingDeduction(index);
    setTempDeduction({
      ...deduction,
      currencyId: deduction.currencyId || deduction.currency?._id || defaultCurrency?._id || null,
      currency: deduction.currency || defaultCurrency || null
    });
    setShowDeductionDialog(true);
  };

  const handleDeductionChange = (field, value) => {
    setTempDeduction(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveDeduction = () => {
    // Validate deduction
    if (!tempDeduction.reason || !tempDeduction.amount) {
      toastify("Reason and amount are required for deductions", "warning");
      return;
    }

    const newDeductions = [...securityDeposit.deductions];
    const deductionCurrencyId = tempDeduction.currencyId || defaultCurrency?._id || null;
    const deductionCurrency = tempDeduction.currency || defaultCurrency || null;

    if (editingDeduction !== null) {
      // Update existing deduction
      newDeductions[editingDeduction] = {
        ...tempDeduction,
        currencyId: deductionCurrencyId,
        currency: deductionCurrency
      };
    } else {
      // Add new deduction
      newDeductions.push({
        ...tempDeduction,
        currencyId: deductionCurrencyId,
        currency: deductionCurrency
      });
    }

    setSecurityDeposit(prev => ({
      ...prev,
      deductions: newDeductions
    }));

    setShowDeductionDialog(false);

    // Recalculate refund amount
    setTimeout(calculateRefundAmount, 0);
  };

  const archiveDeduction = (index) => {
    const newDeductions = [...securityDeposit.deductions];
    // Mark deduction as archived instead of deleting
    newDeductions[index] = {
      ...newDeductions[index],
      isArchived: true,
      archivedAt: new Date().toISOString(),
      archivedBy: 'Current User'
    };

    setSecurityDeposit(prev => ({
      ...prev,
      deductions: newDeductions.filter(deduction => !deduction.isArchived)
    }));

    // Recalculate refund amount
    setTimeout(calculateRefundAmount, 0);

    // Show confirmation message
    toastify("Deduction archived successfully. It will be kept in history.", "info");
  };

  // Functions for the unpaid invoices integration
  const handleAddInvoiceDeduction = (invoice) => {
    // Validate invoice
    if (!invoice || !invoice._id) {
      toastify("Invalid invoice data", "error");
      return;
    }

    // Check if already added
    const isAlreadyAdded = securityDeposit.deductions.some(
      d => d.invoiceId && d.invoiceId.toString() === invoice._id.toString()
    );

    if (isAlreadyAdded) {
      toastify("This invoice has already been added as a deduction", "warning");
      return;
    }

    // Create a deduction from this invoice
    const newDeduction = {
      reason: `Unpaid Invoice #${invoice.invoiceNumber || 'Unknown'}`,
      amount: typeof invoice.balance === 'number' ? invoice.balance : 0,
      description: `Outstanding balance for ${invoice.formattedType || 'Unknown'} invoice dated ${invoice.formattedDate || 'Unknown'}. Items: ${invoice.itemsSummary || 'None'}`,
      invoiceId: invoice._id // Store reference to the invoice
    };

    const newDeductions = [...securityDeposit.deductions, newDeduction];

    // Update state
    setSecurityDeposit(prev => ({
      ...prev,
      deductions: newDeductions
    }));

    // Recalculate refund amount
    setTimeout(calculateRefundAmount, 0);

    toastify(`Added invoice #${invoice.invoiceNumber || 'Unknown'} as a deduction`, "success");
  };

  const handleAddAllInvoiceDeductions = () => {
    // Ensure unpaidInvoices is an array
    const invoiceList = Array.isArray(unpaidInvoices) ? unpaidInvoices : [];

    if (invoiceList.length === 0) {
      toastify("No unpaid invoices to add", "info");
      return;
    }

    // Get existing deduction invoice IDs
    const existingInvoiceIds = securityDeposit.deductions
      .filter(d => d.invoiceId)
      .map(d => d.invoiceId.toString());

    // Filter out invoices that have already been added
    const newInvoices = invoiceList.filter(
      invoice => invoice._id && !existingInvoiceIds.includes(invoice._id.toString())
    );

    if (newInvoices.length === 0) {
      toastify("All invoices have already been added as deductions", "info");
      return;
    }

    // Create deductions for each invoice
    const newDeductions = newInvoices.map(invoice => ({
      reason: `Unpaid Invoice #${invoice.invoiceNumber || 'Unknown'}`,
      amount: typeof invoice.balance === 'number' ? invoice.balance : 0,
      description: `Outstanding balance for ${invoice.formattedType || 'Unknown'} invoice dated ${invoice.formattedDate || 'Unknown'}. Items: ${invoice.itemsSummary || 'None'}`,
      invoiceId: invoice._id
    })).filter(deduction => deduction.amount > 0);

    // Combine with existing deductions
    const allDeductions = [...securityDeposit.deductions, ...newDeductions];

    // Update state
    setSecurityDeposit(prev => ({
      ...prev,
      deductions: allDeductions
    }));

    setShowInvoiceDeductionsDialog(false);

    // Recalculate refund amount
    setTimeout(calculateRefundAmount, 0);

    toastify(`Added ${newDeductions.length} invoice deductions`, "success");
  };

  // Signature handlers
  const handleSavePropertyManagerSignature = () => {
    if (propertyManagerSigRef.current && !propertyManagerSigRef.current.isEmpty()) {
      const signatureData = propertyManagerSigRef.current.toDataURL();
      setFormData(prev => ({
        ...prev,
        signatures: {
          ...prev.signatures,
          propertyManager: {
            signature: signatureData,
            date: new Date()
          }
        }
      }));
      toastify('Property Manager signature saved successfully', 'success');
    } else {
      toastify('Please sign before saving', 'warning');
    }
  };

  const handleClearPropertyManagerSignature = () => {
    if (propertyManagerSigRef.current) {
      propertyManagerSigRef.current.clear();
    }
    if (propertyManagerFileRef.current) {
      propertyManagerFileRef.current.clear();
    }
    setFormData(prev => ({
      ...prev,
      signatures: {
        ...prev.signatures,
        propertyManager: {
          signature: '',
          date: null
        }
      }
    }));
  };

  const handleSaveCustomerSignature = () => {
    if (customerSigRef.current && !customerSigRef.current.isEmpty()) {
      const signatureData = customerSigRef.current.toDataURL();
      setFormData(prev => ({
        ...prev,
        signatures: {
          ...prev.signatures,
          customer: {
            signature: signatureData,
            date: new Date()
          }
        }
      }));
      toastify('Customer signature saved successfully', 'success');
    } else {
      toastify('Please sign before saving', 'warning');
    }
  };

  const handleClearCustomerSignature = () => {
    if (customerSigRef.current) {
      customerSigRef.current.clear();
    }
    if (customerFileRef.current) {
      customerFileRef.current.clear();
    }
    setFormData(prev => ({
      ...prev,
      signatures: {
        ...prev.signatures,
        customer: {
          signature: '',
          date: null
        }
      }
    }));
  };

  const handlePropertyManagerImageUpload = (e) => {
    if (e.files && e.files[0]) {
      const file = e.files[0];
      if (!file.type.startsWith('image/')) {
        toastify('Please upload an image file', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          signatures: {
            ...prev.signatures,
            propertyManager: {
              signature: event.target.result,
              date: new Date()
            }
          }
        }));
        if (propertyManagerSigRef.current) {
          propertyManagerSigRef.current.clear();
        }
        toastify('Property Manager signature image uploaded successfully', 'success');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCustomerImageUpload = (e) => {
    if (e.files && e.files[0]) {
      const file = e.files[0];
      if (!file.type.startsWith('image/')) {
        toastify('Please upload an image file', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          signatures: {
            ...prev.signatures,
            customer: {
              signature: event.target.result,
              date: new Date()
            }
          }
        }));
        if (customerSigRef.current) {
          customerSigRef.current.clear();
        }
        toastify('Customer signature image uploaded successfully', 'success');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClose = () => {
    setDisplayDialog(false);

    // If onClose prop is provided, call it
    if (onClose) {
      setTimeout(() => onClose(), 300);
    } else {
      // Otherwise, use router navigation
      setTimeout(() => navigate('/facility/handover_management', {
        state: { activeTab: handover?.handoverType === 'MoveIn' ? 'moveIn' : 'moveOut' }
      }), 300);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.handoverDate) {
        toastify("Handover date is required", "warning");
        return;
      }

      // Validate attachment names
      const attachmentsWithoutNames = formData.attachments.filter(att => !att.name || att.name.trim() === '');
      if (attachmentsWithoutNames.length > 0) {
        toastify('Please provide a name for all attachments', "warning");
        return;
      }

      setSubmitting(true);

      // Upload images for items and get URLs
      const itemsWithImageUrls = await Promise.all(
        formData.items.map(async (item) => {
          const cleanedItem = { ...item };

          // Remove manual IDs (numeric IDs that cause ObjectId cast errors)
          if (typeof cleanedItem._id === 'number' || typeof cleanedItem.id === 'number') {
            delete cleanedItem._id;
            delete cleanedItem.id;
          }

          // Upload images and get URLs
          try {
            if (item.images && item.images.length > 0) {
              cleanedItem.images = await uploadItemImages(item.images);
            } else {
              cleanedItem.images = [];
            }
          } catch (error) {
            console.error('Error uploading images for item:', item.name, error);
            // Continue with empty images if upload fails
            cleanedItem.images = [];
            toastify(`Warning: Images for ${item.name} could not be uploaded`, 'warn');
          }

          return cleanedItem;
        })
      );

      // Upload attachments and get URLs
      let attachmentsWithUrls = [];
      try {
        attachmentsWithUrls = await uploadAttachments(formData.attachments);
      } catch (error) {
        console.error('Error uploading attachments:', error);
        toastify('Warning: Some attachments could not be uploaded', 'warn');
        // Use original attachments without file objects if upload fails
        attachmentsWithUrls = formData.attachments.map(att => ({
          name: att.name,
          fileName: att.fileName,
          fileUrl: att.fileUrl,
          uploadDate: att.uploadDate
        }));
      }

      // Prepare payload
      const payload = {
        ...formData,
        handoverDate: formData.handoverDate.toISOString(),
        items: itemsWithImageUrls,
        attachments: attachmentsWithUrls,
        // Add security deposit for move-out handovers
        ...(handover?.handoverType === 'MoveOut' ? { securityDeposit } : {})
      };

      // Send request
      if (!facilityId) {
        toastify("No facility selected", "error");
        return;
      }

      const response = await makeRequest2(`${update_handover}/${facilityId}/${id}`, "PUT", payload);

      if (response?.success) {
        toastify("Handover updated successfully", "success");
        handleClose();
      } else {
        throw new Error(response?.error || "Failed to update handover");
      }
    } catch (error) {
      // console.error('Error updating handover:', error);
      toastify(`Error: ${error.message || 'Failed to update handover'}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Render customer and unit information header
  const renderHandoverHeader = () => {
    if (!handover) return null;

    const customer = handover.customerId;
    const unit = handover.unitId;

    return (
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body p-4">
          <div className="row g-4">
            {/* Handover Type & Status */}
            <div className="col-md-3">
              <div className="d-flex align-items-center mb-2">
                <i className={`ti ${handover.handoverType === 'MoveIn' ? 'ti-home-plus' : 'ti-home-minus'} text-primary me-2`} style={{ fontSize: '1.2rem' }}></i>
                <h6 className="mb-0 text-primary fw-bold">
                  {handover.handoverType === 'MoveIn' ? 'Move-In' : 'Move-Out'} Handover
                </h6>
              </div>
              <div className="d-flex align-items-center">
                <span className={`badge ${handover.status === 'Completed' ? 'bg-success' :
                    handover.status === 'Draft' ? 'bg-warning' :
                      handover.status === 'Disputed' ? 'bg-danger' : 'bg-secondary'
                  } me-2`}>
                  {handover.status || 'Draft'}
                </span>
                <small className="text-muted">
                  {handover.handoverDate ? new Date(handover.handoverDate).toLocaleDateString() : 'No date set'}
                </small>
              </div>
            </div>

            {/* Customer Information */}
            <div className="col-md-4">
              <div className="d-flex align-items-center mb-2">
                <i className="ti ti-user text-success me-2" style={{ fontSize: '1.2rem' }}></i>
                <h6 className="mb-0 text-success fw-bold">Customer Details</h6>
              </div>
              <div>
                <div className="fw-medium text-dark">
                  {customer && typeof customer === 'object'
                    ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                    : customer || 'Unknown Customer'
                  }
                </div>
                {customer && typeof customer === 'object' && (
                  <>
                    {customer.email && (
                      <div className="small text-muted">
                        <i className="ti ti-mail me-1"></i>
                        {customer.email}
                      </div>
                    )}
                    {customer.phoneNumber && (
                      <div className="small text-muted">
                        <i className="ti ti-phone me-1"></i>
                        {customer.phoneNumber}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Unit Information */}
            <div className="col-md-4">
              <div className="d-flex align-items-center mb-2">
                <i className="ti ti-building text-info me-2" style={{ fontSize: '1.2rem' }}></i>
                <h6 className="mb-0 text-info fw-bold">Unit Details</h6>
              </div>
              <div>
                <div className="fw-medium text-dark">
                  {unit && typeof unit === 'object'
                    ? unit.name || unit._id
                    : unit || 'Unknown Unit'
                  }
                </div>
                {unit && typeof unit === 'object' && (
                  <>
                    {unit.unitType && (
                      <div className="small text-muted">
                        <i className="ti ti-category me-1"></i>
                        {unit.unitType}
                      </div>
                    )}
                    {unit.division && (
                      <div className="small text-muted">
                        <i className="ti ti-building-estate me-1"></i>
                        {unit.division}
                      </div>
                    )}
                    {unit.floorUnitNo && (
                      <div className="small text-muted">
                        <i className="ti ti-stairs me-1"></i>
                        Floor: {unit.floorUnitNo}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="col-md-1">
              <div className="text-center">
                <div className="fw-bold text-primary" style={{ fontSize: '1.5rem' }}>
                  {formData.items?.length || 0}
                </div>
                <div className="small text-muted">Items</div>
              </div>
            </div>
          </div>

          {/* Additional Information Row */}
          <div className="row mt-3 pt-3 border-top">
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <i className="ti ti-key text-warning me-2"></i>
                <span className="small text-muted">Keys: </span>
                <span className="fw-medium ms-1">{formData.keysHandedOver || 0}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <i className="ti ti-calendar text-secondary me-2"></i>
                <span className="small text-muted">Created: </span>
                <span className="fw-medium ms-1">
                  {handover.createdAt ? new Date(handover.createdAt).toLocaleDateString() : 'Unknown'}
                </span>
              </div>
            </div>
            {handover.handoverType === 'MoveOut' && securityDeposit.amount > 0 && (
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-end">
                  <i className="ti ti-shield-dollar text-success me-2"></i>
                  <span className="small text-muted">Security Deposit: </span>
                  <span className="fw-bold text-success ms-1">
                    ${parseFloat(securityDeposit.amount || 0).toFixed(2)}
                  </span>
                  <span className="text-muted mx-2">→</span>
                  <span className="small text-muted">Refund: </span>
                  <span className="fw-bold text-primary">
                    ${parseFloat(securityDeposit.refundAmount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render functions for tabs
  const renderBasicInfoTab = () => {
    return (
      <div className="p-fluid">
        <div className="row">
          <div className="col-md-6">
            <div className="field">
              <label htmlFor="handoverDate" className="fw-bold">
                <i className="ti ti-calendar me-2 text-primary"></i>
                Handover Date
              </label>
              <Calendar
                id="handoverDate"
                value={formData.handoverDate}
                onChange={(e) => handleFormChange('handoverDate', e.value)}
                showIcon
                dateFormat="dd/mm/yy"
                className="w-100"
                placeholder="Select handover date"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="field">
              <label htmlFor="status" className="fw-bold">
                <i className="ti ti-flag me-2 text-success"></i>
                Status
              </label>
              <Dropdown
                id="status"
                value={formData.status}
                options={statusOptions}
                onChange={(e) => handleFormChange('status', e.value)}
                placeholder="Select Status"
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="field">
              <label htmlFor="keysHandedOver" className="fw-bold">
                <i className="ti ti-key me-2 text-warning"></i>
                Keys Handed Over
              </label>
              <InputNumber
                id="keysHandedOver"
                value={formData.keysHandedOver}
                onValueChange={(e) => handleFormChange('keysHandedOver', e.value)}
                min={0}
                max={20}
                className="w-100"
                placeholder="Number of keys"
              />
            </div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="notes" className="fw-bold">
            <i className="ti ti-notes me-2 text-info"></i>
            Notes & Comments
          </label>
          <InputTextarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleFormChange('notes', e.target.value)}
            rows={5}
            className="w-100"
            placeholder="Add any additional notes or comments about this handover..."
          />
        </div>

      </div>
    );
  };

  const renderMeterReadingsTab = () => {
    const unitId = handover?.unitId ? (typeof handover.unitId === 'object' ? handover.unitId._id : handover.unitId) : null;

    return (
      <div className="p-fluid">
        <div className="row">
          <div className="col-md-4">
            <label className="fw-medium mb-2">Electricity Reading</label>
            <div className="d-flex gap-2">
              <InputNumber
                value={formData.meterReadings.electricity.reading}
                onValueChange={(e) => handleMeterReadingChange('electricity', e.value)}
                min={0}
                showButtons
                className="w-100"
              />
              {powerMeterData && (
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-sm"
                  tooltip="Fetch Latest Reading"
                  onClick={() => fetchPowerMeterData(unitId)}
                  disabled={!unitId || meterDataLoading}
                />
              )}
            </div>
            {powerMeterData && (
              <small className="text-success">
                Smart meter: {powerMeterData.meterNumber || 'Connected'}
              </small>
            )}
            {previousReadings.electricity.length > 0 && (
              <div className="mt-2">
                <small className="text-muted d-block">Previous readings:</small>
                {previousReadings.electricity.slice(0, 3).map((reading, index) => (
                  <small key={index} className="text-muted d-block">
                    {reading.reading} kWh ({new Date(reading.date).toLocaleDateString()})
                  </small>
                ))}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <label className="fw-medium mb-2">Water Reading</label>
            <div className="d-flex gap-2">
              <InputNumber
                value={formData.meterReadings.water.reading}
                onValueChange={(e) => handleMeterReadingChange('water', e.value)}
                min={0}
                showButtons
                className="w-100"
              />
              {waterMeterData && (
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-sm"
                  tooltip="Fetch Latest Reading"
                  onClick={() => fetchWaterMeterData(unitId)}
                  disabled={!unitId || meterDataLoading}
                />
              )}
            </div>
            {waterMeterData && (
              <small className="text-success">
                Smart meter: {waterMeterData.meterNumber}
              </small>
            )}
            {previousReadings.water.length > 0 && (
              <div className="mt-2">
                <small className="text-muted d-block">Previous readings:</small>
                {previousReadings.water.slice(0, 3).map((reading, index) => (
                  <small key={index} className="text-muted d-block">
                    {reading.reading} m³ ({new Date(reading.date).toLocaleDateString()})
                  </small>
                ))}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <label className="fw-medium mb-2">Gas Reading</label>
            <InputNumber
              value={formData.meterReadings.gas.reading}
              onValueChange={(e) => handleMeterReadingChange('gas', e.value)}
              min={0}
              showButtons
              className="w-100"
            />
            <small className="text-muted">Manual entry only</small>
          </div>
        </div>

        {/* Previous Readings Section */}
        {(previousReadings.water.length > 0 || previousReadings.electricity.length > 0) && (
          <div className="mt-4">
            <h6 className="fw-medium mb-3">Previous Readings</h6>
            <div className="row">
              {previousReadings.electricity.length > 0 && (
                <div className="col-md-6">
                  <h6 className="fw-medium mb-2">Electricity</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reading</th>
                          <th>Date</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousReadings.electricity.map((reading, index) => (
                          <tr key={index}>
                            <td>{reading.reading} kWh</td>
                            <td>{new Date(reading.date).toLocaleDateString()}</td>
                            <td>
                              <Tag
                                severity={reading.type.toLowerCase().includes('in') ? 'success' : 'info'}
                                value={reading.type}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {previousReadings.water.length > 0 && (
                <div className="col-md-6">
                  <h6 className="fw-medium mb-2">Water</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reading</th>
                          <th>Date</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousReadings.water.map((reading, index) => (
                          <tr key={index}>
                            <td>{reading.reading} m³</td>
                            <td>{new Date(reading.date).toLocaleDateString()}</td>
                            <td>
                              <Tag
                                severity={reading.type.toLowerCase().includes('in') ? 'success' : 'info'}
                                value={reading.type}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInventoryTab = () => {
    const conditionTemplate = (rowData) => {
      if (!rowData.condition) return null;

      const conditionConfig = {
        'Excellent': { class: 'bg-success', icon: 'ti-star' },
        'Good': { class: 'bg-success', icon: 'ti-check' },
        'Fair': { class: 'bg-warning', icon: 'ti-minus' },
        'Poor': { class: 'bg-danger', icon: 'ti-alert-triangle' },
        'Damaged': { class: 'bg-danger', icon: 'ti-x' }
      };

      const config = conditionConfig[rowData.condition] || { class: 'bg-secondary', icon: 'ti-help' };

      return (
        <span className={`badge ${config.class} d-flex align-items-center gap-1`}>
          <i className={`ti ${config.icon}`} style={{ fontSize: '0.8rem' }}></i>
          {rowData.condition}
        </span>
      );
    };

    const quantityTemplate = (rowData) => {
      return (
        <span className="fw-medium">{rowData.quantity || 1}</span>
      );
    };

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h6 className="mb-1 fw-bold">
              <i className="ti ti-package text-primary me-2"></i>
              Inventory Items ({formData.items?.length || 0})
            </h6>
            <p className="text-muted small mb-0">
              Manage handover inventory items, condition, and repair costs.
            </p>
          </div>
          <Button
            label="Add Inventory Item"
            icon="ti ti-plus"
            className="p-button-sm p-button-primary"
            onClick={openNewItemDialog}
          />
        </div>

        <DataTable
          value={formData.items}
          stripedRows
          emptyMessage={
            <div className="text-center p-4">
              <i className="ti ti-package" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
              <p className="text-muted mt-2 mb-0">No inventory items found</p>
              <small className="text-muted">Inventory items are set during handover creation</small>
            </div>
          }
          className="shadow-sm"
        >
          <Column
            field="name"
            header={
              <div className="d-flex align-items-center">
                <i className="ti ti-tag me-2"></i>Item Name
              </div>
            }
            sortable
          />
          <Column
            field="category"
            header={
              <div className="d-flex align-items-center">
                <i className="ti ti-category me-2"></i>Category
              </div>
            }
            sortable
          />
          <Column
            field="condition"
            header={
              <div className="d-flex align-items-center">
                <i className="ti ti-health-recognition me-2"></i>
                {handover?.handoverType === 'MoveOut' ? 'Original Condition' : 'Condition'}
              </div>
            }
            body={conditionTemplate}
            sortable
          />
          {handover?.handoverType === 'MoveOut' && (
            <Column
              field="currentCondition"
              header={
                <div className="d-flex align-items-center">
                  <i className="ti ti-refresh me-2"></i>Current Condition
                </div>
              }
              body={(rowData) => {
                const currentCondition = rowData.currentCondition || rowData.condition;
                const severity = ['Excellent', 'Good'].includes(currentCondition) ? 'success' :
                  currentCondition === 'Fair' ? 'warning' : 'danger';
                return <Tag value={currentCondition} severity={severity} />;
              }}
              sortable
            />
          )}
          <Column
            field="quantity"
            header={
              <div className="d-flex align-items-center">
                <i className="ti ti-hash me-2"></i>Qty
              </div>
            }
            body={quantityTemplate}
            style={{ width: '80px' }}
          />
          {handover?.handoverType === 'MoveOut' && (
            <Column
              header={
                <div className="d-flex align-items-center">
                  <i className="ti ti-currency-dollar me-2"></i>Repair Cost
                </div>
              }
              body={(rowData) => {
                const repairCost = rowData.repairCost || 0;
                if (repairCost > 0) {
                  const repairCurrency = rowData.currency?.currencyShortCode || defaultCurrencyCode;
                  return (
                    <span className="fw-medium text-danger">
                      {repairCurrency} {repairCost.toFixed(2)}
                    </span>
                  );
                } else {
                  return (
                    <span className="text-muted">
                      No repair cost
                    </span>
                  );
                }
              }}
              style={{ width: '120px' }}
            />
          )}
          <Column
            header={
              <div className="d-flex align-items-center">
                <i className="ti ti-photo me-2"></i>Images
              </div>
            }
            body={(rowData) => (
              <div>
                {rowData.images && rowData.images.length > 0 ? (
                  <Button
                    label={`${rowData.images.length}`}
                    icon="ti ti-photo"
                    className="p-button-sm p-button-info"
                    onClick={() => {
                      setSelectedItemImages(rowData.images);
                      setSelectedItemName(rowData.name);
                      setShowImageDialog(true);
                    }}
                    tooltip="View images"
                  />
                ) : (
                  <Tag value="No images" severity="secondary" />
                )}
              </div>
            )}
            style={{ width: '100px' }}
          />
          <Column
            field="notes"
            header={
              <div className="d-flex align-items-center">
                <i className="ti ti-note me-2"></i>Notes
              </div>
            }
            style={{ maxWidth: '200px' }}
            body={(rowData) => (
              <div className="text-truncate" title={rowData.notes || ''} style={{ maxWidth: '200px' }}>
                {rowData.notes || '-'}
              </div>
            )}
          />
          <Column
            header="Actions"
            body={(rowData, { rowIndex }) => (
              <div className="d-flex gap-2">
                <Button
                  icon="ti ti-pencil"
                  className="p-button-sm p-button-warning"
                  onClick={() => openEditItemDialog(rowData, rowIndex)}
                />
                <Button
                  icon="ti ti-archive"
                  className="p-button-sm p-button-secondary"
                  onClick={() => archiveItem(rowIndex)}
                />
              </div>
            )}
            style={{ width: '8rem' }}
          />
        </DataTable>

        <Dialog
          header={editingItem !== null ? "Edit Inventory Item" : "Add Inventory Item"}
          visible={showItemDialog}
          style={{ width: '70vw', maxWidth: '800px' }}
          onHide={() => setShowItemDialog(false)}
          modal
        >
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="fw-medium mb-2">Item Name *</label>
                <InputText
                  value={tempItem.name}
                  onChange={(e) => handleItemChange('name', e.target.value)}
                  placeholder="Enter item name"
                  className="w-100"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="fw-medium mb-2">Category *</label>
                <Dropdown
                  value={tempItem.category}
                  options={categoryOptions}
                  onChange={(e) => handleItemChange('category', e.value)}
                  placeholder="Select category"
                  className="w-100"
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="fw-medium mb-2">Condition *</label>
                <Dropdown
                  value={tempItem.condition}
                  options={conditionOptions}
                  onChange={(e) => handleItemChange('condition', e.value)}
                  className="w-100"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="fw-medium mb-2">Quantity</label>
                <InputNumber
                  value={tempItem.quantity}
                  onValueChange={(e) => handleItemChange('quantity', e.value || 1)}
                  min={1}
                  showButtons
                  className="w-100"
                />
              </div>
            </div>
          </div>

          {handover?.handoverType === 'MoveOut' && (
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="fw-medium mb-2">Repair Cost</label>
                  <InputNumber
                    value={tempItem.repairCost}
                    onValueChange={(e) => handleItemChange('repairCost', e.value || 0)}
                    mode="currency"
                    currency={tempItem.currency?.currencyShortCode || defaultCurrencyCode}
                    locale="en-US"
                    min={0}
                    className="w-100"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="fw-medium mb-2">Currency</label>
                  <Dropdown
                    value={tempItem.currencyId || defaultCurrency?._id || null}
                    options={currencies.map(curr => ({
                      label: `${curr.currencyName} (${curr.currencyShortCode})`,
                      value: curr._id
                    }))}
                    onChange={(e) => {
                      const selectedCurrency = currencies.find(curr => curr._id === e.value);
                      handleItemChange('currencyId', e.value);
                      handleItemChange('currency', selectedCurrency || defaultCurrency || null);
                    }}
                    placeholder="Select currency"
                    className="w-100"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mb-3">
            <label className="fw-medium mb-2">Notes</label>
            <InputTextarea
              value={tempItem.notes}
              onChange={(e) => handleItemChange('notes', e.target.value)}
              rows={3}
              className="w-100"
            />
          </div>

          <div className="mb-3">
            <label className="fw-medium mb-2">Images</label>
            <FileUpload
              mode="basic"
              name="itemImages"
              accept="image/*"
              maxFileSize={2000000}
              chooseLabel="Add Images"
              auto
              customUpload
              uploadHandler={handleItemImageUpload}
              className="w-100"
            />
            {tempItem.images && tempItem.images.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {tempItem.images.map((img, index) => (
                  <div key={index} className="position-relative">
                    <img
                      src={img.preview || img}
                      alt="Item"
                      style={{ width: '90px', height: '90px', objectFit: 'cover' }}
                      className="rounded border"
                    />
                    <Button
                      icon="ti ti-x"
                      className="p-button-rounded p-button-danger p-button-sm position-absolute"
                      style={{ top: '-8px', right: '-8px' }}
                      onClick={() => removeItemImage(index)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowItemDialog(false)}
            />
            <Button
              label={editingItem !== null ? "Update Item" : "Add Item"}
              className="p-button-success"
              onClick={saveItem}
            />
          </div>
        </Dialog>
      </div>
    );
  };

  const [newAttachment, setNewAttachment] = useState({
    name: '',
    file: null
  });

  const handleAddAttachment = () => {
    if (!newAttachment.name || !newAttachment.name.trim()) {
      toastify('Please enter an attachment name', 'warning');
      return;
    }
    if (!newAttachment.file) {
      toastify('Please select a file to upload', 'warning');
      return;
    }

    const attachment = {
      id: Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      name: newAttachment.name.trim(),
      fileName: newAttachment.file.name,
      fileUrl: URL.createObjectURL(newAttachment.file),
      file: newAttachment.file,
      uploadDate: new Date().toISOString(),
      size: newAttachment.file.size
    };

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, attachment]
    }));

    // Reset form
    setNewAttachment({ name: '', file: null });
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }

    toastify('Attachment added successfully', 'success');
  };

  const renderAttachmentsTab = () => {
    return (
      <div>
        <div className="mb-4">
          <h6 className="mb-3 fw-bold">
            <i className="ti ti-plus text-primary me-2"></i>
            Add New Attachment
          </h6>
          <div className="card border-0 bg-light">
            <div className="card-body p-4">
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-medium">
                    Attachment Name/Label *
                  </label>
                  <InputText
                    value={newAttachment.name}
                    onChange={(e) => setNewAttachment(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Kitchen Photo, Lease Document"
                    className="w-100"
                  />
                  <small className="text-muted">
                    Give this file a descriptive name to identify it easily
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-medium">
                    Select File *
                  </label>
                  <FileUpload
                    ref={fileUploadRef}
                    name="attachment"
                    accept="image/*,application/pdf,.doc,.docx"
                    maxFileSize={5000000}
                    customUpload={true}
                    auto={false}
                    chooseLabel="Choose File"
                    uploadLabel=""
                    cancelLabel=""
                    onSelect={(e) => {
                      if (e.files && e.files.length > 0) {
                        setNewAttachment(prev => ({ ...prev, file: e.files[0] }));
                      }
                    }}
                    onClear={() => setNewAttachment(prev => ({ ...prev, file: null }))}
                  />
                  <small className="text-muted">
                    Max 5MB • Images, PDF, Word
                  </small>
                </div>
              </div>
              <div className="text-end">
                <Button
                  label="Add Attachment"
                  icon="ti ti-plus"
                  onClick={handleAddAttachment}
                  className="p-button-primary"
                  disabled={!newAttachment.name || !newAttachment.file}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <h6 className="fw-bold mb-3">
            <i className="ti ti-files text-success me-2"></i>
            Uploaded Attachments ({formData.attachments?.length || 0})
          </h6>
          {formData.attachments && formData.attachments.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '5%' }}></th>
                    <th style={{ width: '35%' }}>Name/Label</th>
                    <th style={{ width: '30%' }}>File Name</th>
                    <th style={{ width: '15%' }}>Date</th>
                    <th style={{ width: '15%' }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.attachments.map((attachment, index) => (
                    <tr key={index}>
                      <td>
                        <i className={`ti ${attachment.fileName?.toLowerCase().endsWith('.pdf') ? 'ti-file-type-pdf text-danger' :
                            attachment.fileName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? 'ti-photo text-primary' :
                              'ti-file text-secondary'
                          }`} style={{ fontSize: '1.5rem' }}></i>
                      </td>
                      <td>
                        <InputText
                          value={attachment.name || ''}
                          onChange={(e) => updateAttachmentName(index, e.target.value)}
                          placeholder="Enter name"
                          className="w-100"
                          size="small"
                        />
                      </td>
                      <td>
                        <small className="text-muted">{attachment.fileName || 'No filename'}</small>
                      </td>
                      <td>
                        <small className="text-muted">
                          {attachment.uploadDate ? new Date(attachment.uploadDate).toLocaleDateString() : 'N/A'}
                        </small>
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          {attachment.fileUrl && (
                            <Button
                              icon="ti ti-eye"
                              className="p-button-sm p-button-text p-button-info"
                              onClick={() => window.open(attachment.fileUrl, '_blank')}
                              tooltip="View"
                            />
                          )}
                          <Button
                            icon="ti ti-trash"
                            className="p-button-sm p-button-text p-button-danger"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                attachments: prev.attachments.filter((_, i) => i !== index)
                              }));
                              toastify('Attachment removed', 'info');
                            }}
                            tooltip="Remove"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center p-5 border border-dashed rounded bg-light">
              <i className="ti ti-files text-muted" style={{ fontSize: '3rem' }}></i>
              <p className="text-muted mt-3 mb-0">No attachments yet</p>
              <small className="text-muted">Add attachments using the form above</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSecurityDepositTab = () => {
    if (handover?.handoverType !== 'MoveOut') {
      return <p>Security deposit is only applicable for move-out handovers.</p>;
    }

    const actionTemplate = (rowData, { rowIndex }) => {
      return (
        <div className="d-flex gap-2">
          <Button
            icon="ti ti-pencil"
            className="p-button-sm p-button-warning"
            onClick={() => openEditDeductionDialog(rowData, rowIndex)}
          />
          <Button
            icon="ti ti-archive"
            className="p-button-sm p-button-warning"
            onClick={() => archiveDeduction(rowIndex)}
            tooltip="Archive deduction (keeps history)"
          />
        </div>
      );
    };

    return (
      <div>
        <div className="mb-4">
          <div className="field">
            <label htmlFor="depositAmount">Original Deposit Amount</label>
            <InputNumber
              id="depositAmount"
              value={securityDeposit.amount}
              onValueChange={(e) => {
                handleSecurityDepositChange('amount', e.value);
              }}
              mode="currency"
              currency={defaultCurrencyCode}
              locale="en-US"
              className="w-100"
            />
          </div>
        </div>

        <h5>Deductions</h5>
        <div className="mb-3 d-flex justify-content-end gap-2">
          <Button
            label="Unpaid Invoices"
            icon="ti ti-file-invoice"
            className="p-button-info"
            onClick={() => setShowInvoiceDeductionsDialog(true)}
            loading={isLoadingInvoices}
          />
          <Button
            label="Add Deduction"
            icon="ti ti-plus"
            onClick={openNewDeductionDialog}
          />
        </div>

        <DataTable
          value={securityDeposit.deductions}
          stripedRows
          emptyMessage="No deductions added"
        >
          <Column field="reason" header="Reason" />
          <Column
            field="amount"
            header="Amount"
            body={(rowData) => {
              const currencyCode = rowData.currency?.currencyShortCode || defaultCurrencyCode;
              return `${currencyCode} ${parseFloat(rowData.amount || 0).toFixed(2)}`;
            }}
          />
          <Column
            header="Currency"
            body={(rowData) => rowData.currency?.currencyShortCode || defaultCurrencyCode}
          />
          <Column field="description" header="Description" />
          <Column body={actionTemplate} header="Actions" style={{ width: '8rem' }} />
        </DataTable>

        <div className="mt-4 p-3 bg-light border rounded">
          <div className="row">
            <div className="col-md-4">
              <strong>Original Deposit:</strong>
            </div>
            <div className="col-md-8 text-end">
              {defaultCurrencyCode} {parseFloat(securityDeposit.amount || 0).toFixed(2)}
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <strong>Total Deductions:</strong>
            </div>
            <div className="col-md-8 text-end">
              -{defaultCurrencyCode} {securityDeposit.deductions.reduce((sum, deduction) => sum + (parseFloat(deduction.amount) || 0), 0).toFixed(2)}
            </div>
          </div>
          <div className="row fw-bold mt-2 pt-2 border-top">
            <div className="col-md-4">
              <strong>Refund Amount:</strong>
            </div>
            <div className="col-md-8 text-end">
              {defaultCurrencyCode} {parseFloat(securityDeposit.refundAmount || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Deduction Dialog */}
        <Dialog
          header={editingDeduction !== null ? "Edit Deduction" : "Add New Deduction"}
          visible={showDeductionDialog}
          style={{ width: '500px' }}
          modal
          onHide={() => setShowDeductionDialog(false)}
          footer={(
            <div>
              <Button label="Cancel" icon="ti ti-times" className="p-button-text" onClick={() => setShowDeductionDialog(false)} />
              <Button label="Save" icon="ti ti-check" onClick={saveDeduction} />
            </div>
          )}
        >
          <div className="p-fluid">
            <div className="mb-3">
              <div className="field">
                <label htmlFor="deductionReason">Reason*</label>
                <InputText
                  id="deductionReason"
                  value={tempDeduction.reason}
                  onChange={(e) => handleDeductionChange('reason', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <div className="field">
                <label htmlFor="deductionAmount">Amount*</label>
                <InputNumber
                  id="deductionAmount"
                  value={tempDeduction.amount}
                  onValueChange={(e) => handleDeductionChange('amount', e.value)}
                  mode="currency"
                  currency={defaultCurrencyCode}
                  locale="en-US"
                  min={0}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <div className="field">
                <label htmlFor="deductionCurrency">Currency*</label>
                <Dropdown
                  id="deductionCurrency"
                  value={tempDeduction.currencyId || defaultCurrency?._id || null}
                  options={currencies.map(curr => ({
                    label: `${curr.currencyName} (${curr.currencyShortCode})`,
                    value: curr._id
                  }))}
                  onChange={(e) => {
                    const selectedCurrency = currencies.find(curr => curr._id === e.value);
                    handleDeductionChange('currencyId', e.value);
                    handleDeductionChange('currency', selectedCurrency || defaultCurrency || null);
                  }}
                  placeholder="Select currency"
                  className="w-100"
                />
              </div>
            </div>

            <div className="mb-3">
              <div className="field">
                <label htmlFor="deductionDescription">Description</label>
                <InputTextarea
                  id="deductionDescription"
                  value={tempDeduction.description}
                  onChange={(e) => handleDeductionChange('description', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>
        </Dialog>

        {/* Unpaid Invoices Dialog */}
        <Dialog
          header="Unpaid Invoices"
          visible={showInvoiceDeductionsDialog}
          style={{ width: '80vw', maxWidth: '900px' }}
          onHide={() => setShowInvoiceDeductionsDialog(false)}
          modal
          resizable={false}
          footer={
            <div className="text-end">
              <Button
                label="Cancel"
                icon="ti ti-times"
                className="p-button-text me-2"
                onClick={() => setShowInvoiceDeductionsDialog(false)}
              />
              <Button
                label="Add All Invoices as Deductions"
                icon="ti ti-plus"
                className="p-button-success"
                onClick={handleAddAllInvoiceDeductions}
                disabled={!Array.isArray(unpaidInvoices) || unpaidInvoices.length === 0}
              />
            </div>
          }
        >
          {isLoadingInvoices ? (
            <div className="d-flex justify-content-center p-4">
              <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
              <span className="ms-3 font-medium">Loading invoices...</span>
            </div>
          ) : (
            <>
              {!Array.isArray(unpaidInvoices) || unpaidInvoices.length === 0 ? (
                <div className="p-4 text-center">
                  <i className="ti ti-file-invoice mb-3" style={{ fontSize: '3rem', opacity: 0.5 }}></i>
                  <h5>No Unpaid Invoices</h5>
                  <p className="text-muted">There are no unpaid invoices for this unit and customer.</p>
                </div>
              ) : (
                <div className="p-fluid">
                  <p className="mb-3">The following unpaid invoices were found for this tenant. You can filter by invoice types and add them as deductions from the security deposit.</p>

                  {/* Invoice Summary */}
                  {unpaidInvoices.length > 0 && (
                    <div className="mb-4 p-3 bg-light border border-light rounded">
                      <div className="row">
                        <div className="col-md-6">
                          <h6 className="fw-bold mb-2">
                            <i className="ti ti-file-invoice me-2"></i>
                            Invoice Summary ({unpaidInvoices.length} invoices)
                          </h6>
                          <div className="d-flex flex-wrap gap-2">
                            {invoiceTypes.map(type => {
                              const count = unpaidInvoices.filter(inv =>
                                inv.type === type.value ||
                                inv.formattedType?.toLowerCase() === type.label.toLowerCase()
                              ).length;
                              if (count === 0) return null;
                              return (
                                <span key={type.value} className={`badge bg-${type.color} d-flex align-items-center gap-1`}>
                                  <i className={type.icon} style={{ fontSize: '0.8rem' }}></i>
                                  {type.label}: {count}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <h6 className="fw-bold mb-2">
                            <i className="ti ti-currency-dollar me-2"></i>
                            Total Outstanding
                          </h6>
                          <div className="fs-5 fw-bold text-danger">
                            {(unpaidInvoices.length > 0 && unpaidInvoices[0].currency?.code) || defaultCurrencyCode}{' '}
                            {unpaidInvoices.reduce((total, inv) => total + (inv.balance || 0), 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <DataTable
                    value={unpaidInvoices}
                    stripedRows
                    className="mb-3"
                    emptyMessage="No unpaid invoices found"
                  >
                    <Column field="invoiceNumber" header="Invoice #" />
                    <Column field="formattedType" header="Type"
                      body={(rowData) => {
                        const typeInfo = invoiceTypes.find(t =>
                          t.value === rowData.type ||
                          t.label.toLowerCase() === rowData.formattedType?.toLowerCase()
                        );
                        return (
                          <span className={`badge bg-${typeInfo?.color || 'secondary'} d-flex align-items-center gap-1`} style={{ width: 'fit-content' }}>
                            <i className={`${typeInfo?.icon || 'ti-file'}`} style={{ fontSize: '0.8rem' }}></i>
                            {rowData.formattedType || rowData.type || 'Unknown'}
                          </span>
                        );
                      }}
                    />
                    <Column field="formattedDate" header="Issue Date" />
                    <Column field="formattedDueDate" header="Due Date" />
                    <Column field="status" header="Status"
                      body={(rowData) => (
                        <Tag
                          value={rowData.status}
                          severity={
                            rowData.status === 'Unpaid' ? 'warning' :
                              rowData.status === 'Overdue' ? 'danger' :
                                rowData.status === 'Partially Paid' ? 'info' : 'secondary'
                          }
                        />
                      )}
                    />
                    <Column field="itemsSummary" header="Items" style={{ maxWidth: '200px' }}
                      body={(rowData) => (
                        <div className="text-truncate" style={{ maxWidth: '200px' }} title={rowData.itemsSummary || ''}>
                          {rowData.itemsSummary || ''}
                        </div>
                      )}
                    />
                    <Column field="balance" header="Balance"
                      body={(rowData) => (
                        <span className="font-bold">
                          {rowData.currency?.code || defaultCurrencyCode} {(rowData.balance || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </span>
                      )}
                    />
                    <Column body={(rowData) => {
                      if (!rowData || !rowData._id) return null;

                      // Check if this invoice is already added as a deduction
                      const isAlreadyAdded = securityDeposit.deductions.some(
                        d => d.invoiceId && d.invoiceId.toString() === rowData._id.toString()
                      );

                      return (
                        isAlreadyAdded ? (
                          <Button
                            label="Already Added"
                            icon="ti ti-check"
                            className="p-button-outlined p-button-success p-button-sm"
                            disabled
                          />
                        ) : (
                          <Button
                            label="Add as Deduction"
                            icon="ti ti-plus"
                            className="p-button-primary p-button-sm"
                            onClick={() => handleAddInvoiceDeduction(rowData)}
                          />
                        )
                      );
                    }} header="Actions" />
                  </DataTable>

                  <div className="alert alert-warning p-3">
                    <i className="ti ti-info-circle me-2"></i>
                    <span>
                      Adding these invoices as deductions will reduce the security deposit refund amount.
                      This does not automatically mark the invoices as paid.
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </Dialog>
      </div>
    );
  };

  const renderSignaturesTab = () => {
    return (
      <div className="p-fluid">
        <div className="alert alert-info mb-4">
          <i className="ti ti-info-circle me-2"></i>
          <strong>Note:</strong> Signatures are required when handover status is set to 'Completed'. You can either draw a signature or upload an image.
        </div>

        {/* Property Manager Signature Section */}
        <div className="border rounded p-4 mb-4">
          <h6 className="fw-medium mb-3">
            <i className="ti ti-user-shield me-2 text-primary"></i>
            Property Manager Signature {formData.status === 'Completed' && <span className="text-danger">*</span>}
          </h6>

          {/* Draw Signature */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Draw Signature</label>
            <div className="signature-canvas-container" style={{ border: '2px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
              <SignatureCanvas
                ref={propertyManagerSigRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
              />
            </div>
            <div className="d-flex gap-2 mt-2">
              <Button
                label="Clear Canvas"
                icon="ti ti-eraser"
                className="p-button-outlined p-button-secondary p-button-sm"
                onClick={() => {
                  if (propertyManagerSigRef.current) {
                    propertyManagerSigRef.current.clear();
                  }
                }}
              />
              <Button
                label="Save Drawn Signature"
                icon="ti ti-check"
                className="p-button-success p-button-sm"
                onClick={handleSavePropertyManagerSignature}
              />
            </div>
          </div>

          {/* Upload Signature Image */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Or Upload Signature Image</label>
            <FileUpload
              ref={propertyManagerFileRef}
              mode="basic"
              name="propertyManagerSignature"
              accept="image/*"
              maxFileSize={2000000}
              onSelect={handlePropertyManagerImageUpload}
              chooseLabel="Choose Image"
              className="w-100"
              auto
            />
            <small className="text-muted">Max file size: 2MB. Supported formats: JPG, PNG, GIF</small>
          </div>

          {/* Status Display */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              {formData.signatures.propertyManager.signature && (
                <div className="text-success">
                  <i className="ti ti-check-circle me-1"></i>
                  Signature saved on {formData.signatures.propertyManager.date && new Date(formData.signatures.propertyManager.date).toLocaleString()}
                </div>
              )}
            </div>
            <div>
              {formData.signatures.propertyManager.signature && (
                <Button
                  label="Remove Signature"
                  icon="ti ti-trash"
                  className="p-button-outlined p-button-danger p-button-sm"
                  onClick={handleClearPropertyManagerSignature}
                />
              )}
            </div>
          </div>

          {/* Saved Signature Preview */}
          {formData.signatures.propertyManager.signature && (
            <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <small className="text-muted d-block mb-2 fw-medium">Saved Signature:</small>
              <div className="bg-white p-2 rounded" style={{ border: '1px solid #dee2e6' }}>
                <img
                  src={formData.signatures.propertyManager.signature}
                  alt="Property Manager Signature"
                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px', display: 'block', margin: '0 auto' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Customer Signature Section */}
        <div className="border rounded p-4">
          <h6 className="fw-medium mb-3">
            <i className="ti ti-user me-2 text-primary"></i>
            Customer Signature {formData.status === 'Completed' && <span className="text-danger">*</span>}
          </h6>

          {/* Draw Signature */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Draw Signature</label>
            <div className="signature-canvas-container" style={{ border: '2px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
              <SignatureCanvas
                ref={customerSigRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
              />
            </div>
            <div className="d-flex gap-2 mt-2">
              <Button
                label="Clear Canvas"
                icon="ti ti-eraser"
                className="p-button-outlined p-button-secondary p-button-sm"
                onClick={() => {
                  if (customerSigRef.current) {
                    customerSigRef.current.clear();
                  }
                }}
              />
              <Button
                label="Save Drawn Signature"
                icon="ti ti-check"
                className="p-button-success p-button-sm"
                onClick={handleSaveCustomerSignature}
              />
            </div>
          </div>

          {/* Upload Signature Image */}
          <div className="mb-3">
            <label className="fw-medium mb-2">Or Upload Signature Image</label>
            <FileUpload
              ref={customerFileRef}
              mode="basic"
              name="customerSignature"
              accept="image/*"
              maxFileSize={2000000}
              onSelect={handleCustomerImageUpload}
              chooseLabel="Choose Image"
              className="w-100"
              auto
            />
            <small className="text-muted">Max file size: 2MB. Supported formats: JPG, PNG, GIF</small>
          </div>

          {/* Status Display */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              {formData.signatures.customer.signature && (
                <div className="text-success">
                  <i className="ti ti-check-circle me-1"></i>
                  Signature saved on {formData.signatures.customer.date && new Date(formData.signatures.customer.date).toLocaleString()}
                </div>
              )}
            </div>
            <div>
              {formData.signatures.customer.signature && (
                <Button
                  label="Remove Signature"
                  icon="ti ti-trash"
                  className="p-button-outlined p-button-danger p-button-sm"
                  onClick={handleClearCustomerSignature}
                />
              )}
            </div>
          </div>

          {/* Saved Signature Preview */}
          {formData.signatures.customer.signature && (
            <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <small className="text-muted d-block mb-2 fw-medium">Saved Signature:</small>
              <div className="bg-white p-2 rounded" style={{ border: '1px solid #dee2e6' }}>
                <img
                  src={formData.signatures.customer.signature}
                  alt="Customer Signature"
                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px', display: 'block', margin: '0 auto' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const dialogFooter = (
    <div className="d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center text-muted">
        <i className="ti ti-info-circle me-2"></i>
        <small>
          {handover?.handoverType === 'MoveIn' ? 'Move-in' : 'Move-out'} handover for{' '}
          {handover?.customerId && typeof handover.customerId === 'object'
            ? `${handover.customerId.firstName} ${handover.customerId.lastName}`
            : 'customer'}
        </small>
      </div>
      <div className="d-flex gap-2">
        <Button
          label="Cancel"
          icon="ti ti-times"
          className="p-button-secondary p-button-outlined"
          onClick={handleClose}
          disabled={submitting}
        />
        <Button
          label={submitting ? "Saving..." : "Save Changes"}
          icon={submitting ? "ti ti-loader" : "ti ti-device-floppy"}
          onClick={handleSubmit}
          loading={submitting}
          className="p-button-primary"
        />
      </div>
    </div>
  );

  return (
    <Layout>
      <Toast ref={toast} />
      <Dialog
        header={`Edit ${handover?.handoverType === 'MoveIn' ? 'Move-In' : 'Move-Out'} Handover`}
        visible={displayDialog}
        style={{ width: '90vw', maxWidth: '1200px' }}
        onHide={handleClose}
        footer={dialogFooter}
        maximizable
      >
        {loading ? (
          <div className="text-center p-5">
            <i className="ti ti-spin ti-spinner" style={{ fontSize: '2rem' }}></i>
            <p className="mt-3">Loading handover details...</p>
          </div>
        ) : (
          <>
            {/* Enhanced Header with Customer and Unit Information */}
            {renderHandoverHeader()}

            {/* Enhanced Tab Interface */}
            <TabView
              activeIndex={activeTab}
              onTabChange={(e) => setActiveTab(e.index)}
              className="custom-tabview"
            >
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-info-circle me-2"></i>
                    Basic Info
                  </div>
                }
              >
                {renderBasicInfoTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-gauge me-2"></i>
                    Meter Readings
                  </div>
                }
              >
                {renderMeterReadingsTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-package me-2"></i>
                    Inventory ({formData.items?.length || 0})
                  </div>
                }
              >
                {renderInventoryTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-paperclip me-2"></i>
                    Attachments ({formData.attachments?.length || 0})
                  </div>
                }
              >
                {renderAttachmentsTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-writing me-2"></i>
                    Signatures
                    {formData.status === 'Completed' && <span className="text-danger ms-1">*</span>}
                  </div>
                }
              >
                {renderSignaturesTab()}
              </TabPanel>
              {handover?.handoverType === 'MoveOut' && (
                <TabPanel
                  header={
                    <div className="d-flex align-items-center">
                      <i className="ti ti-shield-dollar me-2"></i>
                      Security Deposit
                    </div>
                  }
                >
                  {renderSecurityDepositTab()}
                </TabPanel>
              )}
            </TabView>
          </>
        )}
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        header={`Images - ${selectedItemName}`}
        visible={showImageDialog}
        style={{ width: '80vw', maxWidth: '900px' }}
        onHide={() => setShowImageDialog(false)}
        dismissableMask
      >
        <div className="row g-3">
          {selectedItemImages.length > 0 ? (
            selectedItemImages.map((imageUrl, index) => (
              <div key={index} className="col-md-6">
                <div className="card">
                  <img
                    src={`${backend_url}${imageUrl}`}
                    alt={`${selectedItemName} - Image ${index + 1}`}
                    style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain' }}
                    className="card-img-top"
                  />
                  <div className="card-body">
                    <small className="text-muted">Image {index + 1}</small>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12 text-center py-4">
              <p className="text-muted">No images available</p>
            </div>
          )}
        </div>
      </Dialog>
    </Layout>
  );
};

export default EditHandover;
